import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CreditCard, CheckCircle2, Clock, AlertCircle, XCircle } from 'lucide-react';
import { useI18n } from '@/hooks/useI18n';
import { useAuth } from '@/hooks/useAuth';
import { useFullProfile } from '@/hooks/useFullProfile';
import { toast } from '@/hooks/useToast';
import { supabase } from '@/integrations/supabase/client';

type StripeAccountStatus = 'not_connected' | 'pending' | 'active' | 'restricted';

const StripeSection = () => {
  const { t } = useI18n();
  const { userRole } = useAuth();
  const { profileData, invalidateProfile } = useFullProfile();

  const [stripeStatus, setStripeStatus] = useState<StripeAccountStatus>('not_connected');
  const [stripeChargesEnabled, setStripeChargesEnabled] = useState<boolean>(false);
  const [stripeOnboarding, setStripeOnboarding] = useState(false);
  const [stripeStatusLoading, setStripeStatusLoading] = useState(false);

  useEffect(() => {
    const pd = profileData?.private_data;
    if (!pd) return;
    setStripeStatus(pd.stripe_account_status || 'not_connected');
    setStripeChargesEnabled(pd.stripe_charges_enabled || false);
  }, [profileData]);

  // Handle Stripe return URL params after onboarding
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const oauthCode = searchParams.get('code');
    const isStripeReturn = searchParams.get('stripe_return') === '1' || searchParams.get('stripe_refresh') === '1';

    if (oauthCode) {
      // OAuth callback: exchange code for account ID
      window.history.replaceState(null, '', '/profile?tab=account-settings');
      handleOAuthCallback(oauthCode);
    } else if (isStripeReturn) {
      // Express callback: sync status from Stripe
      window.history.replaceState(null, '', '/profile?tab=account-settings');
      syncStripeStatus();
    }
  }, []);

  const syncStripeStatus = async () => {
    setStripeStatusLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('stripe-connect-status', {
        body: {},
      });
      if (error) throw new Error(error.message);
      const result = data as { stripe_account_status: StripeAccountStatus; stripe_charges_enabled: boolean } | null;
      if (result) {
        setStripeStatus(result.stripe_account_status);
        setStripeChargesEnabled(result.stripe_charges_enabled);
        invalidateProfile();
        if (result.stripe_charges_enabled) {
          toast({ title: t('agency.stripe.messages.accountActivated'), description: t('agency.stripe.messages.accountActivatedDesc') });
        } else if (result.stripe_account_status === 'pending') {
          toast({ title: t('agency.stripe.messages.onboardingPending'), description: t('agency.stripe.messages.onboardingPendingDesc'), variant: 'default' });
        }
      }
    } catch (err: any) {
      toast({ title: t('agency.stripe.messages.statusError'), description: err.message, variant: 'destructive' });
    } finally {
      setStripeStatusLoading(false);
    }
  };

  const handleStripeConnect = async (mode: 'express' | 'oauth' = 'express') => {
    setStripeOnboarding(true);
    try {
      const origin = window.location.origin;
      const returnUrl = mode === 'oauth'
        ? `${origin}/`
        : `${origin}/?stripe_return=1`;
      const { data, error } = await supabase.functions.invoke('stripe-connect-onboard', {
        body: {
          mode,
          return_url: returnUrl,
          refresh_url: `${origin}/?stripe_refresh=1`,
        },
      });
      if (error) throw new Error(error.message);
      const result = data as { url?: string } | null;
      if (!result?.url) throw new Error(t('agency.stripe.messages.connectError'));
      window.location.href = result.url;
    } catch (err: any) {
      toast({ title: t('agency.stripe.messages.connectError'), description: err.message, variant: 'destructive' });
      setStripeOnboarding(false);
    }
  };

  const handleOAuthCallback = async (code: string) => {
    setStripeStatusLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('stripe-connect-oauth-callback', {
        body: { code },
      });
      if (error) throw new Error(error.message);
      const result = data as { stripe_account_status: StripeAccountStatus; stripe_charges_enabled: boolean } | null;
      if (result) {
        setStripeStatus(result.stripe_account_status);
        setStripeChargesEnabled(result.stripe_charges_enabled);
        invalidateProfile();
        toast({ title: t('agency.stripe.messages.oauthConnected'), description: t('agency.stripe.messages.oauthConnectedDesc') });
      }
    } catch (err: any) {
      toast({ title: t('agency.stripe.messages.oauthError'), description: err.message, variant: 'destructive' });
    } finally {
      setStripeStatusLoading(false);
    }
  };

  // Only show for agency and travel_designer roles (not travel_advisor_member - they use agency's Stripe account)
  if (userRole !== 'agency' && userRole !== 'travel_designer' && userRole !== 'admin') {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-xl font-normal flex items-center gap-2">
          <CreditCard className="h-4 w-4" />
          {t("agency.stripe.title")}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          {/* Status badge */}
          <div className="flex items-center gap-3">
            {stripeStatus === "active" && stripeChargesEnabled ? (
              <>
                <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-green-800">
                    {t("agency.stripe.status.active")}
                  </p>
                  <p className="text-xs text-green-600">
                    {t("agency.stripe.status.active_desc")}
                  </p>
                </div>
              </>
            ) : stripeStatus === "pending" ? (
              <>
                <Clock className="h-5 w-5 text-yellow-500 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-yellow-700">
                    {t("agency.stripe.status.pending")}
                  </p>
                  <p className="text-xs text-yellow-600">
                    {t("agency.stripe.status.pending_desc")}
                  </p>
                </div>
              </>
            ) : stripeStatus === "restricted" ? (
              <>
                <AlertCircle className="h-5 w-5 text-orange-500 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-orange-700">
                    {t("agency.stripe.status.restricted")}
                  </p>
                  <p className="text-xs text-orange-600">
                    {t("agency.stripe.status.restricted_desc")}
                  </p>
                </div>
              </>
            ) : (
              <>
                <XCircle className="h-5 w-5 text-gray-400 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-gray-700">
                    {t("agency.stripe.status.notConnected")}
                  </p>
                  <p className="text-xs text-gray-500">
                    {t("agency.stripe.status.notConnected_desc")}
                  </p>
                </div>
              </>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {stripeStatus === "active" && stripeChargesEnabled ? (
              <Button
                variant="outline"
                size="sm"
                onClick={syncStripeStatus}
                disabled={stripeStatusLoading}
                className="flex items-center gap-1.5"
              >
                {stripeStatusLoading
                  ? t("agency.stripe.onboarding")
                  : t("agency.stripe.manageAccount")}
              </Button>
            ) : (
              <>
                {(stripeStatus === "pending" || stripeStatus === "restricted") && (
                  <div className="flex flex-col gap-2">
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => handleStripeConnect('express')}
                      disabled={stripeOnboarding}
                      className="flex items-center gap-1.5"
                    >
                      {stripeOnboarding
                        ? t("agency.stripe.onboarding")
                        : t("agency.stripe.continueOnboarding")}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={syncStripeStatus}
                      disabled={stripeStatusLoading}
                      className="flex items-center gap-1.5"
                    >
                      {stripeStatusLoading
                        ? t("agency.stripe.verifying")
                        : t("agency.stripe.verifyStatus")}
                    </Button>
                  </div>
                )}
                {stripeStatus === "not_connected" && (
                  <div className="flex flex-col items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleStripeConnect('oauth')}
                      disabled={stripeOnboarding}
                      className="flex items-center gap-1.5"
                    >
                      {stripeOnboarding
                        ? t("agency.stripe.onboarding")
                        : t("agency.stripe.connectExisting")}
                    </Button>
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => handleStripeConnect('express')}
                      disabled={stripeOnboarding}
                      className="flex items-center gap-1.5"
                    >
                      {stripeOnboarding
                        ? t("agency.stripe.onboarding")
                        : t("agency.stripe.connectNew")}
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <p className="text-xs text-gray-400 mt-4">
          {t("agency.stripe.paymentNote")}
        </p>
      </CardContent>
    </Card>
  );
};

export default StripeSection;
